<template>
    <Teleport to="body">
        <Transition name="sidebar-fade">
            <button v-if="goal" type="button" class="fixed inset-0 z-30 w-full cursor-default bg-black/40 md:hidden" aria-label="Close goal details" @click="emit('close')" />
        </Transition>
        <!-- FIXME: Use BaseDrawer when it supports non-modal desktop interaction, mobile geometry, and nested focus handoffs. -->
        <Transition name="sidebar-slide">
            <FocusScope as-child loop trapped>
                <div
                    v-if="goal"
                    data-dismiss-ignore
                    class="fixed inset-x-0 bottom-0 flex w-full max-h-[90dvh] flex-col rounded-t-2xl border-t border-border bg-surface shadow-2xl pb-[var(--mobile-nav-total-height,0px)] md:inset-y-0 md:right-0 md:left-auto md:w-[440px] md:max-h-none md:rounded-none md:border-t-0 md:border-l md:pb-0"
                    :class="isNestedDialogOpen ? 'z-20' : 'z-40'"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="goal-detail-sidebar-title">
                    <!-- Header -->
                    <div ref="drawerHeader" class="flex items-center justify-between gap-3 px-6 py-5 border-b border-border">
                        <div class="flex items-center gap-2 min-w-0">
                            <h2 id="goal-detail-sidebar-title" class="body text-ink min-w-0">{{ goal.title }}</h2>
                        </div>
                        <BaseButton theme="tertiary" size="sm" icon-only aria-label="Close" @click="emit('close')">
                            <i class="bi bi-x-lg text-sm" aria-hidden="true"></i>
                        </BaseButton>
                    </div>

                    <!-- Body -->
                    <div class="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                        <!-- Date -->
                        <div class="flex items-center gap-2">
                            <p class="body-small text-ink-muted">Added on {{ fullDateLabel }}</p>
                        </div>

                        <!-- Description -->
                        <div>
                            <p v-if="goal.description" class="details-big text-ink">{{ goal.description }}</p>
                            <p v-else class="details-big text-ink-muted">No description added.</p>
                        </div>

                        <!-- Actions -->
                        <GoalActionsSection
                            ref="actionsRef"
                            :goal-id="goal.id"
                            :actions="goal.actions"
                            :allow-add-action="!disableActions"
                            @changed="emit('action-added', goal.id)"
                            @all-completed="openCompletionConfirmation"
                            @request-delete-confirm="openActionDeletion" />
                    </div>

                    <!-- Priority button -->
                    <div v-if="showPriorityButton" class="px-6 pb-4">
                        <GoalPriorityToggleButton :is-prioritized="!!goal.prioritized_at" :disabled="isPriorityButtonDisabled" @click="emit('toggle-priority', goal.id)" />
                    </div>

                    <!-- Standard footer -->
                    <div v-if="!hideFooter" class="border-t border-border">
                        <div class="flex items-center gap-3 px-6 py-4">
                            <BaseButton
                                v-if="isSuggestedGoal(goal) && !goal.team_member_goal && currentTab === 'for-you'"
                                theme="primary"
                                size="md"
                                class="md:hidden ml-auto"
                                @click="emit('track-suggestion', goal.id)">
                                Track this goal
                            </BaseButton>
                            <BaseButton theme="secondary" size="md" :disabled="isFetchingChatUrl" @click="goToChat">
                                <span class="relative inline-flex items-center justify-center">
                                    <LoadingSpinner v-if="isFetchingChatUrl" class="h-4 w-4 absolute" />
                                    <span :class="isFetchingChatUrl ? 'invisible' : ''">Dive Deeper</span>
                                </span>
                            </BaseButton>
                            <div class="ml-auto">
                                <BaseDropdownMenu
                                    :sections="moreMenuSections"
                                    side="top"
                                    :side-offset="4"
                                    @select="handleMoreMenuSelect"
                                    @close-auto-focus="handleMoreMenuCloseAutoFocus">
                                    <template #trigger>
                                        <BaseButton ref="moreMenuTrigger" theme="neutral" size="sm" icon-only aria-label="More actions" @click="handleMoreMenuOpened">
                                            <i class="bi bi-three-dots text-sm" aria-hidden="true"></i>
                                        </BaseButton>
                                    </template>
                                </BaseDropdownMenu>
                            </div>
                        </div>
                    </div>

                    <!-- Completed at banner -->
                    <div v-if="goal.completed_at" class="border-t border-border px-6 py-3 flex items-center gap-2">
                        <i class="bi bi-check-circle-fill text-sm text-chip-status-done-text" aria-hidden="true"></i>
                        <p class="body-small text-ink-muted">Completed {{ completedAtLabel }}</p>
                    </div>
                </div>
            </FocusScope>
        </Transition>
    </Teleport>
    <template v-if="goal">
        <SidebarConfirmOverlay
            :open="showCompleteConfirm"
            title="Mark this goal as completed?"
            confirm-text="Complete Goal"
            :is-loading="isCompleting"
            :error-text="completeErrorText"
            :return-focus-to="dialogReturnFocusTo"
            @update:open="handleCompleteOverlayVisibility"
            @confirmed="handleMarkComplete" />
        <BaseDialog
            width="narrow"
            :show-close="false"
            :open="connectionOverlayOpen"
            title="Assign this goal to a team member"
            :disable-close-on-backdrop-click="isAssigningConnection"
            :return-focus-to="dialogReturnFocusTo"
            mobile-height="expanded"
            @update:open="updateAssignmentOpen">
            <BaseNotification v-if="connectionAssignErrorText" theme="error" :text="connectionAssignErrorText" hide-dismiss />
            <ConnectionSearch ref="connectionSearchRef" auto-focus :disabled="isAssigningConnection" @select="selectedConnection = $event" @clear="selectedConnection = null" />
            <template #footer>
                <BaseButton theme="neutral" size="sm" :disabled="isAssigningConnection" @click="updateAssignmentOpen(false)">Cancel</BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="!selectedConnection || isAssigningConnection" :aria-busy="isAssigningConnection" @click="submitConnectionAssign">
                    {{ isAssigningConnection ? "Saving…" : "Save" }}
                </BaseButton>
            </template>
        </BaseDialog>
        <BaseDialog
            width="narrow"
            :show-close="false"
            :open="showDeleteFeedback"
            title="Why are you deleting this goal?"
            :disable-close-on-backdrop-click="isDiscarding"
            :return-focus-to="dialogReturnFocusTo"
            mobile-height="expanded"
            @update:open="updateDeletionOpen">
            <div class="flex flex-col gap-4">
                <BaseNotification v-if="deleteErrorText" theme="error" :text="deleteErrorText" hide-dismiss />
                <DiscardGoalFeedback
                    :goal-id="goal.id"
                    :is-deleting="isDeleting"
                    @update:processing="isSavingDiscardFeedback = $event"
                    @discarded="handleDelete"
                    @cancel="updateDeletionOpen(false)" />
            </div>
        </BaseDialog>
        <BaseDialog
            width="narrow"
            :show-close="false"
            :open="!!pendingDeleteActionId"
            title="Delete this action?"
            description="This cannot be undone."
            :disable-close-on-backdrop-click="isDeletingAction"
            :return-focus-to="dialogReturnFocusTo"
            mobile-height="expanded"
            @update:open="updateActionDeletionOpen">
            <p class="body-small text-ink-muted">This cannot be undone.</p>
            <BaseNotification v-if="deleteActionErrorText" theme="error" :text="deleteActionErrorText" hide-dismiss />
            <template #footer>
                <BaseButton theme="neutral" size="sm" :disabled="isDeletingAction" @click="updateActionDeletionOpen(false)">Cancel</BaseButton>
                <BaseButton theme="danger" size="sm" :disabled="isDeletingAction" :aria-busy="isDeletingAction" @click="handleConfirmDeleteAction">
                    {{ isDeletingAction ? "Deleting…" : "Delete" }}
                </BaseButton>
            </template>
        </BaseDialog>
    </template>
</template>

<script setup lang="ts">
import { useEventListener } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { GoalUpdateRequest } from "~vue/api/generated/goals";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { type Goal } from "~vue/types/goals";
import { formatDate } from "~vue/utils/formatDate";
import { isSuggestedGoal } from "~vue/utils/goals";
import { FocusScope } from "reka-ui";
import { computed, nextTick, ref, shallowRef, watch } from "vue";

import ConnectionSearch from "./ConnectionSearch.vue";
import DiscardGoalFeedback from "./DiscardGoalFeedback.vue";
import GoalActionsSection from "./GoalActionsSection.vue";
import GoalPriorityToggleButton from "./GoalPriorityToggleButton.vue";
import SidebarConfirmOverlay from "./SidebarConfirmOverlay.vue";
import { type DiscussWithNadiaClickedExtraData, JourneysAmplitudeEventNames, type JourneysTab, logJourneysUserInteraction } from "./utils";

const props = defineProps<{
    goal: Goal | null;
    canPrioritize?: boolean;
    hidePriorityButton?: boolean;
    hideFooter?: boolean;
    disableActions?: boolean;
    currentTab?: JourneysTab;
}>();

const emit = defineEmits<{
    close: [];
    "action-added": [goalId: string];
    "goal-completed": [goalId: string];
    "goal-deleted": [goalId: string];
    "goal-updated": [goalId: string];
    "goal-assigned": [goalId: string];
    "goal-confirmed": [goalId: string];
    "toggle-priority": [goalId: string];
    "track-suggestion": [goalId: string];
    edit: [goalId: string];
}>();

const actionsRef = ref<InstanceType<typeof GoalActionsSection> | null>(null);
const pendingDeleteActionId = ref<string | null>(null);
const isDeletingAction = ref(false);
const deleteActionErrorText = ref("");
const moreMenuTrigger = ref<InstanceType<typeof BaseButton> | null>(null);
const drawerHeader = shallowRef<HTMLElement | null>(null);
const dialogReturnFocusTo = shallowRef<HTMLElement | null>(null);

function onEscapeKey(e: KeyboardEvent) {
    if (e.key === "Escape" && props.goal && !e.defaultPrevented && !isNestedDialogOpen.value && !document.querySelector("[data-dismissable-layer]")) {
        emit("close");
    }
}
useEventListener("keydown", onEscapeKey);

async function handleConfirmDeleteAction() {
    const actionId = pendingDeleteActionId.value;
    if (!actionId || isDeletingAction.value) {
        return;
    }
    isDeletingAction.value = true;
    deleteActionErrorText.value = "";
    try {
        await actionsRef.value?.executeDelete(actionId);
        dialogReturnFocusTo.value = drawerHeader.value?.querySelector<HTMLButtonElement>('button[aria-label="Close"]') ?? null;
        pendingDeleteActionId.value = null;
    } catch {
        deleteActionErrorText.value = "Couldn't delete action. Please try again.";
        await nextTick();
        dialogReturnFocusTo.value = actionsRef.value?.$el.querySelector(`[data-goal-action-id="${CSS.escape(actionId)}"] button[aria-label="More actions"]`);
    } finally {
        isDeletingAction.value = false;
    }
}

const showCompleteConfirm = ref(false);
const isCompleting = ref(false);
const showDeleteFeedback = ref(false);
const isDeleting = ref(false);
const isSavingDiscardFeedback = ref(false);
const isDiscarding = computed(() => isDeleting.value || isSavingDiscardFeedback.value);
const isNestedDialogOpen = computed(() => showCompleteConfirm.value || connectionOverlayOpen.value || showDeleteFeedback.value || !!pendingDeleteActionId.value);

function rememberDialogOpener() {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) {
        return;
    }
    const menu = activeElement.closest<HTMLElement>("[role=menu]");
    if (menu?.id) {
        const trigger = document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(menu.id)}"]`) ?? moreMenuTrigger.value?.$el;
        dialogReturnFocusTo.value = trigger instanceof HTMLElement ? trigger : null;
    } else if (!activeElement.closest("[data-dismissable-layer]")) {
        dialogReturnFocusTo.value = activeElement;
    } else if (!dialogReturnFocusTo.value?.isConnected) {
        const trigger = moreMenuTrigger.value?.$el;
        dialogReturnFocusTo.value = trigger instanceof HTMLElement ? trigger : null;
    }
}

function openCompletionConfirmation() {
    rememberDialogOpener();
    showCompleteConfirm.value = true;
}

function openActionDeletion(actionId: string) {
    rememberDialogOpener();
    deleteActionErrorText.value = "";
    pendingDeleteActionId.value = actionId;
}

function updateAssignmentOpen(value: boolean) {
    if (!isAssigningConnection.value) {
        connectionOverlayOpen.value = value;
    }
}

function updateDeletionOpen(value: boolean) {
    if (!isDiscarding.value) {
        showDeleteFeedback.value = value;
    }
}

function updateActionDeletionOpen(value: boolean) {
    if (!value && !isDeletingAction.value) {
        pendingDeleteActionId.value = null;
        deleteActionErrorText.value = "";
    }
}

const { execute: completeGoal, error: completeError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
)
    .patch({ status: "completed" })
    .json<Goal>();

const { execute: deleteGoal, error: deleteError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
).delete();

const completeErrorText = ref("");
const deleteErrorText = ref("");

function handleCompleteOverlayVisibility(open: boolean) {
    if (!open && !isCompleting.value) {
        showCompleteConfirm.value = false;
        completeErrorText.value = "";
    }
}

async function handleMarkComplete() {
    if (isCompleting.value) {
        return;
    }
    isCompleting.value = true;
    completeErrorText.value = "";
    await completeGoal();
    isCompleting.value = false;

    if (completeError.value) {
        completeErrorText.value = "Couldn't complete goal. Please try again.";
        return;
    }

    showCompleteConfirm.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_COMPLETED, { goal_id: props.goal!.id, was_prioritized: !!props.goal!.prioritized_at }, props.currentTab);
    emit("goal-completed", props.goal!.id);
    emit("close");
}

async function handleDelete() {
    if (isDeleting.value) {
        return;
    }
    isDeleting.value = true;
    deleteErrorText.value = "";
    await deleteGoal();
    isDeleting.value = false;

    if (deleteError.value) {
        deleteErrorText.value = "Couldn't delete goal. Please try again.";
        return;
    }

    showDeleteFeedback.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_DISCARDED, { goal_id: props.goal!.id }, props.currentTab);
    emit("goal-deleted", props.goal!.id);
    emit("close");
}

const connectionOverlayOpen = ref(false);
const connectionSearchRef = ref<InstanceType<typeof ConnectionSearch>>();
const selectedConnection = ref<{ id: string; name: string | null; job_title: string | null } | null>(null);
// Don't auto-open in track mode — the user clicks "Assign and track" instead
watch(
    () => props.goal?.id,
    () => {
        connectionOverlayOpen.value = !!props.goal?.team_member_goal && !props.goal?.connection;
    },
);

watch(connectionOverlayOpen, (open: boolean) => {
    if (!open) {
        selectedConnection.value = null;
        connectionAssignErrorText.value = "";
    }
});

const connectionPatch = ref<GoalUpdateRequest>({});
const { execute: patchConnection, error: patchConnectionError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
)
    .patch(connectionPatch)
    .json<Goal>();

const isAssigningConnection = ref(false);
const connectionAssignErrorText = ref("");

async function submitConnectionAssign() {
    if (!selectedConnection.value || isAssigningConnection.value) {
        return;
    }
    connectionPatch.value = {
        connection_id: selectedConnection.value.id,
        team_member_goal: true,
        confirm_goal: true,
    };
    isAssigningConnection.value = true;
    connectionAssignErrorText.value = "";
    await patchConnection();
    isAssigningConnection.value = false;

    if (patchConnectionError.value) {
        connectionAssignErrorText.value = "Couldn't assign this goal. Please try again.";
        return;
    }

    connectionOverlayOpen.value = false;
    logJourneysUserInteraction(
        JourneysAmplitudeEventNames.GOAL_CONNECTION_ASSIGNED,
        {
            goal_id: props.goal!.id,
            is_new_assignment: !props.goal!.connection,
        },
        props.currentTab,
    );
    emit("goal-updated", props.goal!.id);
}

const {
    data: chatData,
    isFetching: isFetchingChatUrl,
    execute: fetchChatUrl,
} = useFetch(
    computed(() => `/goals/${props.goal?.id}/chat/`),
    { immediate: false },
).json<{ coaching_session_url: string }>();

function handleMoreMenuOpened() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_MORE_MENU_OPENED, { goal_id: props.goal?.id }, props.currentTab);
}

const moreMenuSections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (props.goal?.team_member_goal) {
        items.push({ id: "assign", type: "button", label: props.goal.connection ? "Reassign" : "Assign", icon: "bi bi-person" });
    }
    items.push({ id: "edit", type: "button", label: "Edit", icon: "bi bi-pencil" }, { id: "complete", type: "button", label: "Complete", icon: "bi bi-check-circle" });
    return [
        { id: "actions", items },
        { id: "danger", items: [{ id: "delete", type: "button", label: "Delete", icon: "bi bi-trash" }] },
    ];
});

function handleMoreMenuSelect(item: DropdownMenuItem) {
    rememberDialogOpener();
    if (item.id === "assign") {
        connectionOverlayOpen.value = true;
    } else if (item.id === "edit") {
        handleEditClicked();
    } else if (item.id === "complete") {
        openCompletionConfirmation();
    } else if (item.id === "delete") {
        showDeleteFeedback.value = true;
    }
}

// A dialog opened from a menu must keep focus instead of returning it to the menu trigger.
// ConnectionSearch's auto-focus also needs to wait until the menu's focus scope is removed.
function handleMoreMenuCloseAutoFocus(event: Event) {
    if (!isNestedDialogOpen.value) {
        return;
    }
    event.preventDefault();
    if (connectionOverlayOpen.value) {
        globalThis.setTimeout(() => connectionSearchRef.value?.focus(), 0);
    }
}

function handleEditClicked() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_EDIT_CLICKED, { goal_id: props.goal!.id }, props.currentTab);
    emit("edit", props.goal!.id);
}

async function goToChat() {
    const discussData: DiscussWithNadiaClickedExtraData = { goal_id: props.goal?.id, source: "sidebar" };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.DISCUSS_WITH_NADIA_CLICKED, discussData, props.currentTab);
    await fetchChatUrl();
    if (chatData.value?.coaching_session_url) {
        globalThis.location.href = chatData.value.coaching_session_url;
    }
}

const showPriorityButton = computed(() => !props.hidePriorityButton && !props.goal?.team_member_goal && !props.goal?.completed_at);
const isPriorityButtonDisabled = computed(() => !props.goal?.prioritized_at && !props.canPrioritize);

const fullDateLabel = computed(() => (props.goal ? formatDate(props.goal.reference_session_created_at ?? props.goal.created_at, "date-long") : ""));

const completedAtLabel = computed(() => formatDate(props.goal?.completed_at, "date-long"));
</script>

<style scoped>
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
    transform: translateY(100%);
}

@media (min-width: 768px) {
    .sidebar-slide-enter-from,
    .sidebar-slide-leave-to {
        transform: translateX(100%);
    }
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
    transition: transform 300ms ease;
}

.sidebar-fade-enter-from,
.sidebar-fade-leave-to {
    opacity: 0;
}

.sidebar-fade-enter-active,
.sidebar-fade-leave-active {
    transition: opacity 200ms ease;
}
</style>
